import {
  getFirestore,
  collection,
  getDoc,
  deleteDoc,
  QuerySnapshot,
} from "firebase/firestore"
import { updateDoc, onSnapshot } from "firebase/firestore"
import { deleteField, arrayUnion, arrayRemove } from "firebase/firestore"
import { getDocs, query, setDoc } from "firebase/firestore"
import { Timestamp, where, doc } from "firebase/firestore"
import { getActiveInspection } from "./Inspection"
import { getUid } from "services/auth"
import { useEffect, useState } from "react"
import dexie, { MediaType, TransformedSection, ensureDb } from "@/database"
import { getPreferences } from "services/Preferences"
import Time from "@/utils/time"
import { toast } from "sonner"
type GenericType = {
  id: string
  name: string
}
async function upgradeComment(
  id: string,
  comment: Partial<Comment> & Record<string, string>,
  col: string = "comments",
) {
  const document = doc(getFirestore(), `${col}/${comment.id}`)
  const path = `${id}/${comment.sectionID}/${comment.itemID}/${comment.commentID}`
  delete comment.sectionID
  delete comment.itemID
  delete comment.commentID
  comment.path = path
  await updateDoc(document, {
    path,
    id: deleteField(),
    sectionID: deleteField(),
    itemID: deleteField(),
    commentID: deleteField(),
    nid: deleteField(),
  })
  return path
}
export type Comment = {
  id: string //the unique id generated by firebase
  uid: string //the inspector ID
  text: string //text content of a comment
  path: string //the path to the comment id/section/item/comment
  media: string[] //list of all media references
  created: Timestamp //date comment was created
  summary: number //1 for true, 0 for false
  modified?: Timestamp //date comment was update
  summaryText: string
  inspectionID: string
  summaryMedia: string[]
  reInspection: number
}
export type ReportComment = {
  name: string
  media: Partial<MediaType>[]
  index: number
} & Omit<Comment, "media" | "summaryMedia">
export type ReportSection = {
  items: ReportItem[]
  index: number
} & GenericType
export type ReportItem = {
  comments: ReportComment[]
} & GenericType
export type Progress = {
  comments: { done: number; count: number }
  sections: { [key: string]: { count: number; done: number } }
}
export type CommentGroup = {
  //section
  [key: string]: {
    //item
    [key: string]: {
      //comment
      [key: string]: Comment
    }
  }
}
function generateCommentId(path: string) {
  return path.replace(/\//g, "_")
}

export function createComment(
  path: string,
  values: Partial<Comment>,
  col: "comments" | "reinspection_comments" = "comments",
) {
  const db = getFirestore()
  const uid = getUid()
  const created = Timestamp.now()

  const id = generateCommentId(path)
  const document = doc(db, col, id)

  const comment = {
    id,
    inspectionID: path.split("/")[0],
    uid,
    created,
    text: "",
    path,
    media: [],
    ...values,
  }

  return comment as Comment
}
export async function getComment(id: string) {
  const db_fire = getFirestore()
  await ensureDb()
  const document = doc(db_fire, `comments/${id}`)
  let comment: Comment = await dexie.table("comments").get(id)

  if (comment) return comment

  const snapshot = await getDoc(document)
  if (!snapshot.exists()) return null
  comment = snapshot.data() as Comment
  comment.id = snapshot.id
  if (comment) {
    await dexie.table("comments").put(comment)
    return comment
  }
  return null
}
export async function queryComment(
  path: string,
  fn: (comment: Comment) => void = () => { },
) {
  let timeout: any

  const timeoutPromise = new Promise((res, rej) => {
    timeout = setTimeout(async () => {
      clearTimeout(timeout)
      // verify connection before local fallback
      await ensureDb()
      //get comment from dexie if firestore is taking too long
      const comment = (await dexie
        .table("comments")
        .where({ path })
        .first()) as Comment

      if (comment) return res(comment)
      rej(new Error("Layout not found in local database"))
    }, 1500)
  })

  await ensureDb()
  const db_fire = getFirestore()
  const [inspectionID] = path.split("/")
  const w = where("path", "==", path)
  const w2 = where("inspectionID", "==", inspectionID)
  const q = query(collection(db_fire, "comments"), w, w2)

  const snapshots = (await Promise.any([getDocs(q), timeoutPromise])) as
    | QuerySnapshot
    | Comment
  clearTimeout(timeout)

  if ("id" in snapshots) {
    fn(snapshots)
    return snapshots
  }
  if (snapshots.empty) {
    const comment = createComment(path, {})
    fn(comment)
    return comment
  }

  const data = snapshots.docs[0].data()
  const comment = { id: snapshots.docs[0].id, ...data } as Comment
  await dexie.table("comments").put(comment)
  const { metadata } = snapshots.docs[0]
  fn({ id: snapshots.docs[0].id, ...data } as Comment)
  return { id: snapshots.docs[0].id, ...data } as Comment
}
/**
 *
 * @param path location of the comment
 * @param fn returns the comment once fetched
 * @param onChange callback when comment changes from another source
 * @returns unsubscribe function
 */
export function onCommentQuery(
  path: string,
  fn: (comment: Comment) => void,
  onChange: (comment: Comment) => void = () => { },
) {
  let unsubscribe = () => { }
  let isFetching = true

  // 1. Immediately fetch from local Dexie cache
  dexie
    .table("comments")
    .where({ path })
    .first()
    .then(localComment => {
      // If we found local data and haven't gotten server data yet, show it
      if (localComment && isFetching) {
        fn(localComment as Comment)
      }
    })

  // 2. Setup high-level preferences check and subscription
  getPreferences().then(async ({ offline_mode }) => {
    if (offline_mode) {
      isFetching = false
      return
    }

    const db = getFirestore()
    const [inspectionID] = path.split("/")
    const w = where("path", "==", path)
    const w2 = where("inspectionID", "==", inspectionID)
    const q = query(collection(db, "comments"), w, w2)

    // Establishment of a real-time subscription
    unsubscribe = onSnapshot(
      q,
      { includeMetadataChanges: true },
      async snapshots => {
        if (snapshots.empty) {
          // Check if we have a local comment that hasn't been synced yet
          const localComment = (await dexie
            .table("comments")
            .where({ path })
            .first()) as Comment | undefined

          if (localComment) {
            const document = doc(db, "comments", localComment.id)
            setDoc(document, localComment)
            if (isFetching) {
              fn(localComment)
              isFetching = false
            }
            return
          }

          // If server confirms no document exists and we haven't shown anything, create default
          if (isFetching) {
            fn(createComment(path, {}))
            isFetching = false
          }
          return
        }

        const snapshot = snapshots.docs[0]
        const data = snapshot.data()
        const comment = { id: snapshot.id, ...data } as Comment

        // Phase 1: Timestamp validation
        // We PROTECT local data: Local is king unless remote is definitively newer.
        const local = (await dexie
          .table("comments")
          .get(comment.id)) as Comment | undefined
        if (local && local.modified) {
          const localTime = Time.toMillis(local.modified)
          const remoteTime = comment.modified
            ? Time.toMillis(comment.modified)
            : 0

          // If remote is missing a timestamp (stale/legacy) or is older than or equal to local, ignore it.
          if (localTime >= remoteTime) {
            // Local is newer: Don't overwrite local cache. 
            // If we are currently fetching, show the local version.
            if (isFetching) {
              fn(local)
              isFetching = false
            } else if (
              !snapshots.metadata.hasPendingWrites &&
              localTime > remoteTime
            ) {
              const document = doc(db, "comments", local.id)
              updateDoc(document, local as any).catch(console.error)
            }
            return
          }
        }

        // Update local cache with fresh data from server or local writes
        dexie.table("comments").put(comment)

        if (isFetching) {
          fn(comment)
          isFetching = false
        } else {
          onChange(comment)
        }
      },
    )
  })

  // Return a cleanup function that will terminate the subscription once ready
  return () => {
    isFetching = false
    unsubscribe()
  }
}

export async function setComment(
  path: string,
  values: Partial<Comment>,
  col: "comments" | "reinspection_comments" = "comments",
) {
  await ensureDb()
  const db = getFirestore()
  const uid = getUid()
  const created = Timestamp.now()
  const modified = created
  const document = doc(collection(db, col))
  const id = document.id

  const comment = {
    inspectionID: path.split("/")[0],
    uid,
    created,
    modified,
    path,
    media: [],
    ...values,
  }

  await dexie.table(col).put({ id, ...comment })
  setDoc(document, comment)

  return { ...comment, id } as Comment
}
export async function saveComment(
  path: string,
  values: Partial<Comment>,
  col: "comments" | "reinspection_comments" = "comments",
) {
  const comment = await queryComment(path)
  const db = getFirestore()
  const document = doc(db, `${col}/${comment.id}`)
  const modified = Timestamp.now()
  const merged = { ...comment, ...values, modified }

  // Durability: Write locally and remote immediately
  await dexie.table(col).put(merged)
  setDoc(document, merged, { merge: true }).catch(console.error)

  return merged as Comment
}
export async function updateComment(
  comment: Comment,
  update: Partial<Comment>,
  col: "comments" | "reinspection_comments" = "comments",
) {
  const retryUpdate = async () => {
    const { id } = comment
    const modified = Timestamp.now()
    await ensureDb()

    // Load-modify-put: use put to ensure write always succeeds even if the
    // record doesn't yet exist in the local Dexie cache.
    // dexie.update() silently returns 0 and doesn't write anything if the record
    // is not found, causing permanent text loss without any error.
    const existing = await dexie.table(col).get(id)
    const merged = { ...(existing || comment), ...update, modified }
    await dexie.table(col).put(merged)

    const db = getFirestore()
    const document = doc(db, `${col}/${id}`)

    setDoc(document, merged, { merge: true }).catch(console.error)
  }

  try {
    await retryUpdate()
  } catch (error: any) {
    if (error?.name === "DatabaseClosedError") {
      // DB was closed (iOS tab suspension) — use two-attempt retry with backoff
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          await retryUpdate()
          return // Success
        } catch (e: any) {
          if (attempt === 1 && e?.name === "DatabaseClosedError") {
            await new Promise(res => setTimeout(res, 500))
            continue
          }
          console.error(`updateComment retry ${attempt} failed:`, e)
          toast.error(`Save failed: ${e?.message || e}. Please try again.`, { duration: 8000 })
          return
        }
      }
    } else {
      console.error("updateComment failed:", error)
      toast.error(`Failed to update comment: ${error?.message || error}`, { duration: 8000 })
    }
  }
}
export async function addCommentMedia(
  comment: Comment,
  media: string[],
  col: "comments" | "reinspection_comments" = "comments",
) {
  const { id } = comment
  const modified = Timestamp.now()

  // Direct mutation for local dexie state
  await dexie
    .table(col)
    .where({ id })
    .modify((data: Comment) => {
      data.modified = modified
      data.media.push(...media)
    })

  // Optimistic Firestore Update: Send the whole updated media list
  // This is safer than arrayUnion delta if Firestore state and local state are out of sync.
  const updated = await dexie.table(col).get(id) as Comment
  const db = getFirestore()
  const document = doc(db, `${col}/${id}`)

  setDoc(document, updated, { merge: true }).catch(console.error)
}
export async function removeCommentMedia(
  comment: Comment,
  media: string[],
  col: "comments" | "reinspection_comments" = "comments",
) {
  const db_fire = getFirestore()
  const { id } = comment
  const document = doc(db_fire, `${col}/${id}`)
  const modified = Timestamp.now()

  await dexie
    .table(col)
    .where({ id })
    .modify((comment: Comment) => {
      comment.modified = modified
      comment.media = comment.media.filter(name => !media.includes(name))
    })

  updateDoc(document, { media: arrayRemove(...media), modified }).catch(console.error)
}
export async function addSummary(id: string, summaryText: string) {
  const modified = Timestamp.now()
  const update = { summary: 1, summaryText, modified }
  // Write locally first so it survives offline
  const existing = await dexie.table("comments").get(id)
  if (existing) await dexie.table("comments").put({ ...existing, ...update })
  const db = getFirestore()
  const document = doc(db, `comments/${id}`)

  updateDoc(document, update).catch(console.error)
}
export async function addSummaryMedia(id: string, media: string) {
  const modified = Timestamp.now()
  await dexie
    .table("comments")
    .where({ id })
    .modify((data: Comment) => {
      data.modified = modified
      if (!data.summaryMedia) data.summaryMedia = []
      if (!data.summaryMedia.includes(media)) data.summaryMedia.push(media)
    })
  const db = getFirestore()
  const document = doc(db, `comments/${id}`)

  updateDoc(document, {
    summaryMedia: arrayUnion(media),
    modified
  }).catch(console.error)
}
export async function removeSummaryMedia(id: string, media: string) {
  const modified = Timestamp.now()
  await dexie
    .table("comments")
    .where({ id })
    .modify((data: Comment) => {
      data.modified = modified
      data.summaryMedia = (data.summaryMedia || []).filter(m => m !== media)
    })
  const db = getFirestore()
  const document = doc(db, `comments/${id}`)

  updateDoc(document, {
    summaryMedia: arrayRemove(media),
    modified
  }).catch(console.error)
}
export async function removeSummary(id: string) {
  const modified = Timestamp.now()

  // 1. Write locally first: load-modify-put removing summary fields
  const existing = await dexie.table("comments").get(id)
  if (existing) {
    const updated = { ...existing }
    delete updated.summary
    delete updated.summaryText
    delete updated.summaryMedia
    updated.modified = modified
    await dexie.table("comments").put(updated)
  }

  // 2. Optimistic Firestore update
  const db = getFirestore()
  const document = doc(db, `comments/${id}`)
  const update = {
    summary: deleteField(),
    summaryText: deleteField(),
    summaryMedia: deleteField(),
    modified,
  }

  updateDoc(document, update).catch(console.error)
}
export async function getSummaries() {
  const id = getActiveInspection()
  if (!id) return null
  await ensureDb()
  const documents = collection(getFirestore(), "comments")
  const { offline_mode } = await getPreferences()
  //load comments from local database when offline
  if (offline_mode) {
    const commentGroup: CommentGroup = {}
    await dexie
      .table("comments")
      .where({ inspectionID: id, summary: 1 })
      .each((comment: Comment) => {
        const [_, section, item, commentID] = comment.path.split("/")

        // Create nested structures if they don't exist
        commentGroup[section] = commentGroup[section] || {}
        commentGroup[section][item] = commentGroup[section][item] || {}

        // Add the comment to the nested structure
        commentGroup[section][item][commentID] = comment as Comment
      })
    return commentGroup
  }
  const q = query(
    documents,
    where("inspectionID", "==", id),
    where("summary", "==", 1),
  )
  const snapshots = await getDocs(q)
  const commentGroup: CommentGroup = {}

  for (let i = 0; i < snapshots.size; i++) {
    const snapshot = snapshots.docs[i]

    const comment = {
      id: snapshot.id,
      ...snapshot.data(),
    } as Partial<Comment> & Record<string, string>
    /*Old comments don't have a paths but they have properties that can be turned into paths
      this code block is use to update these comments.
      */
    if (!comment.path) comment.path = await upgradeComment(id, comment)

    const [_, section, item, commentID] = comment.path.split("/")

    // Create nested structures if they don't exist
    commentGroup[section] = commentGroup[section] || {}
    commentGroup[section][item] = commentGroup[section][item] || {}

    // Add the comment to the nested structure
    commentGroup[section][item][commentID] = comment as Comment
  }

  return commentGroup
}
export async function getReInspections() {
  const id = getActiveInspection()
  if (!id) return null
  await ensureDb()
  const documents = collection(getFirestore(), "reinspection_comments")
  const { offline_mode } = await getPreferences()
  //load comments from local database when offline
  if (offline_mode) {
    const commentGroup: CommentGroup = {}
    await dexie
      .table("reinspection_comments")
      .where({ inspectionID: id, reInspection: 1 })
      .each((comment: Comment) => {
        const [_, section, item, commentID] = comment.path.split("/")

        // Create nested structures if they don't exist
        commentGroup[section] = commentGroup[section] || {}
        commentGroup[section][item] = commentGroup[section][item] || {}

        // Add the comment to the nested structure
        commentGroup[section][item][commentID] = comment as Comment
      })
    return commentGroup
  }
  const q = query(documents, where("inspectionID", "==", id))
  const snapshots = await getDocs(q)
  const commentGroup: CommentGroup = {}

  for (let i = 0; i < snapshots.size; i++) {
    const snapshot = snapshots.docs[i]

    const comment = {
      id: snapshot.id,
      ...snapshot.data(),
    } as Partial<Comment> & Record<string, string>
    /*Old comments don't have a paths but they have properties that can be turned into paths
      this code block is use to update these comments.
      */

    if (!comment.path) comment.path = await upgradeComment(id, comment)

    const [_, section, item, commentID] = comment.path.split("/")

    // Create nested structures if they don't exist
    commentGroup[section] = commentGroup[section] || {}
    commentGroup[section][item] = commentGroup[section][item] || {}

    // Add the comment to the nested structure
    commentGroup[section][item][commentID] = comment as Comment
  }

  return commentGroup
}
export async function getComments(id = getActiveInspection()) {
  await ensureDb()
  const db = getFirestore()
  const documents = collection(db, "comments")

  const q = query(documents, where("inspectionID", "==", id))
  const { offline_mode } = await getPreferences()

  if (offline_mode) {
    const comments = await dexie
      .table("comments")
      .where({ inspectionID: id })
      .toArray()

    if (!comments.length) return null
    const commentGroup: CommentGroup = {}

    for (let i = 0; i < comments.length; i++) {
      const snapshot = comments[i]

      const comment = {
        id: snapshot.id,
        ...snapshot,
      } as Partial<Comment> & Record<string, string>
      /*Old comments don't have a paths but they have properties that can be turned into paths
      this code block is use to update these comments.
      */
      if (!comment.path) comment.path = await upgradeComment(id, comment)

      const [_, section, item, commentID] = comment.path.split("/")

      // Create nested structures if they don't exist
      commentGroup[section] = commentGroup[section] || {}
      commentGroup[section][item] = commentGroup[section][item] || {}

      // Add the comment to the nested structure
      commentGroup[section][item][commentID] = comment as Comment
    }
    return commentGroup
  }

  const snapshots = await getDocs(q)

  if (snapshots.empty) return null
  const commentGroup: CommentGroup = {}
  const bulkComments = []

  for (let i = 0; i < snapshots.size; i++) {
    const snapshot = snapshots.docs[i]

    const comment = {
      id: snapshot.id,
      ...snapshot.data(),
    } as Partial<Comment> & Record<string, string>
    /*Old comments don't have a paths but they have properties that can be turned into paths
      this code block is use to update these comments.
      */
    if (!comment.path) comment.path = await upgradeComment(id, comment)

    const [_, section, item, commentID] = comment.path.split("/")

    // Create nested structures if they don't exist
    commentGroup[section] = commentGroup[section] || {}
    commentGroup[section][item] = commentGroup[section][item] || {}

    // Add the comment to the nested structure
    commentGroup[section][item][commentID] = comment as Comment

    bulkComments.push(comment)
  }
  await dexie.table("comments").bulkPut(bulkComments)
  return commentGroup
}
export function useGetSummaries(fn: () => void = () => { }) {
  const [comments, setComments] = useState<CommentGroup | null>()

  useEffect(() => {
    let unsubscribe = () => { }
    let isMounted = true

    async function load() {
      const id = getActiveInspection()
      if (!id) return

      const { offline_mode } = await getPreferences()
      if (offline_mode) {
        const data = await getSummaries()
        if (isMounted) setComments(data)
      } else {
        const db = getFirestore()
        const q = query(
          collection(db, "comments"),
          where("inspectionID", "==", id),
          where("summary", "==", 1),
        )

        unsubscribe = onSnapshot(q, async snapshots => {
          const commentGroup: CommentGroup = {}
          for (let i = 0; i < snapshots.size; i++) {
            const snapshot = snapshots.docs[i]
            const comment = { id: snapshot.id, ...snapshot.data() } as any
            if (!comment.path) comment.path = await upgradeComment(id, comment)

            const [_, section, item, commentID] = comment.path.split("/")
            commentGroup[section] = commentGroup[section] || {}
            commentGroup[section][item] = commentGroup[section][item] || {}
            commentGroup[section][item][commentID] = comment as Comment
          }
          if (isMounted) setComments(commentGroup)
        })
      }
    }

    load()

    // Only listen for comment-updated when offline — onSnapshot already handles
    // live updates when online, so attaching the listener unconditionally would
    // trigger a redundant Firestore getDocs on every save.
    let handleUpdate: ((e: any) => void) | null = null
    getPreferences().then(({ offline_mode }) => {
      if (offline_mode) {
        handleUpdate = () => load()
        window.addEventListener("comment-updated", handleUpdate)
      }
    })

    return () => {
      isMounted = false
      unsubscribe()
      if (handleUpdate) window.removeEventListener("comment-updated", handleUpdate)
    }
  }, [])


  useEffect(() => {
    if (comments) fn()
  }, [comments])

  return comments
}
export function useGetReInspection(fn: () => void = () => { }) {
  const [comments, setComments] = useState<CommentGroup | null>()

  useEffect(() => {
    let unsubscribe = () => { }
    let isMounted = true

    async function load() {
      const id = getActiveInspection()
      if (!id) return



      const data = await getReInspections()
      console.log(data);

      if (isMounted && data) setComments(data)

      const db = getFirestore()
      const q = query(
        collection(db, "reinspection_comments"),
        where("inspectionID", "==", id)
      )
      // Note: Re-inspection only shows if reInspection property matches, but we must mimic getReInspections
      // Wait, getReInspections fetches ALL reinspection_comments and then the local dexie filters?
      // Let's check getReInspections implementation for Firestore query:
      // const q = query(documents, where("inspectionID", "==", id))

      unsubscribe = onSnapshot(q, async (snapshots) => {
        const commentGroup: CommentGroup = {}
        for (let i = 0; i < snapshots.size; i++) {
          const snapshot = snapshots.docs[i]
          const comment = { id: snapshot.id, ...snapshot.data() } as any
          if (!comment.path) comment.path = await upgradeComment(id, comment, "reinspection_comments")

          const [_, section, item, commentID] = comment.path.split("/")
          commentGroup[section] = commentGroup[section] || {}
          commentGroup[section][item] = commentGroup[section][item] || {}
          commentGroup[section][item][commentID] = comment as Comment
        }
        if (isMounted) setComments(commentGroup)
      })

    }

    load()

    return () => {
      isMounted = false
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    if (comments) fn()
  }, [comments])

  return comments
}
export async function getProgress(id = getActiveInspection()) {
  const document = doc(getFirestore(), `progress/${id}`)
  let progress: Progress = await dexie.table("progress").get(id)

  if (progress) return progress

  const snapshot = await getDoc(document)
  if (!snapshot.exists()) return null
  progress = snapshot.data() as Progress
  if (progress) {
    // Use put() instead of add() — add() throws ConstraintError if the key already exists
    await dexie.table("progress").put({ id, ...progress })
    return progress
  }
  return null
}
export async function setProgress(section: string, progress: Progress) {
  const id = getActiveInspection()
  const document = doc(getFirestore(), `layouts/${id}`)
  // Using put() instead of add() — add() throws ConstraintError if key exists
  await dexie.table("progress").put({ id, [`${section}`]: progress })
  setDoc(document, { [`${section}`]: progress }, { merge: true })
  return
}
export async function updateProgress(section: string, progress: Progress) {
  const id = getActiveInspection()
  const document = doc(getFirestore(), `layouts/${id}`)

  await dexie.table("progress").update(id, { [`${section}`]: progress })
  updateDoc(document, { [`${section}`]: progress }).catch(console.error)
  // switch (action) {
  //   case "increment":
  //     updateDoc(document, {
  //       [`sections.${section}.progress.done`]: increment(count || 1),
  //     })
  //     break
  //   case "decrement":
  //     updateDoc(document, {
  //       [`sections.${section}.progress.done`]: increment(count || -1),
  //     })
  //     break

  //   default:
  //     break
  // }
}
export async function calculateProgress(
  sections: TransformedSection[],
  id?: string,
) {
  const snapshots = await getComments(id)
  const progress: Progress = { comments: { done: 0, count: 0 }, sections: {} } // { done: 0, count: 0 }
  for (let s = 0; s < sections.length; s++) {
    const section = sections[s]
    const { items } = section

    progress.sections[section.id] = { count: 0, done: 0 }

    for (let i = 0; i < items.length; i++) {
      const { comments } = items[i]
      for (let c = 0; c < comments.length; c++) {
        const field = comments[c]
        progress.comments.count++
        progress.sections[section.id].count++
        if (
          !snapshots ||
          !snapshots[section.id] ||
          !snapshots[section.id][items[i].id] ||
          !snapshots[section.id][items[i].id][field.id]
        ) {
          continue
        }

        const comment = snapshots[section.id][items[i].id][field.id]

        if (comment.text) {
          progress.comments.done++
          progress.sections[section.id].done++
          // progress.done++
        }
      }
    }
  }

  //save progress here?
  return progress
}

//Bandage code for re-inspection feature
export async function createReInspectionComment(comment: Partial<Comment>) {
  const db = getFirestore()

  let id = comment.id
  if (comment.path) {
    id = generateCommentId(comment.path)
  } else if (!id) {
    id = doc(collection(db, "reinspection_comments")).id
  }

  const document = doc(db, "reinspection_comments", id)

  comment.id = id
    ; (comment as any).lid = id // Fixes IndexedDB DataError for keyPath match
  comment.media = []
  delete comment.reInspection

  // Adding check to ensure lid matches ID for primary key schema
  await dexie.table("reinspection_comments").put({ ...comment, lid: id })
  setDoc(document, comment)

  return comment as Comment
}
export async function updateReInspectionComment(
  comment: Comment,
  update: Partial<Comment>,
) {
  const { id } = comment
  const modified = Timestamp.now()
  const existing = await dexie.table("reinspection_comments").get(id)
  const merged = { ...(existing || comment), ...update, modified, inspectionID: comment.inspectionID, lid: id }

  await dexie.table("reinspection_comments").put(merged)

  const db = getFirestore()
  const document = doc(db, `reinspection_comments/${id}`)

  setDoc(document, merged, { merge: true }).catch(console.error)
}
export async function setReInspectionComment(
  id: string,
  update: Partial<Comment>,
) {
  const modified = Timestamp.now()
  const existing = await dexie.table("reinspection_comments").get(id)
  const merged = { ...(existing || {}), ...update, id, lid: id, modified }

  await dexie.table("reinspection_comments").put(merged)

  const db = getFirestore()
  const document = doc(db, `reinspection_comments/${id}`)

  setDoc(document, merged, { merge: true }).catch(console.error)
}
export async function removeReinspection(id: string) {
  const db = getFirestore()
  const modified = Timestamp.now()
  const document = doc(db, `reinspection_comments/${id}`)

  await dexie.table("reinspection_comments").delete(id)
  await deleteDoc(document)

  updateDoc(doc(getFirestore(), `comments/${id}`), {
    reInspection: deleteField(),
    modified,
  })
}
